import useGetDashboardInvoiceStats from '@/api/queries/dashboard/useGetDashboardInvoiceStats';
import useGetDashboardProposalStats from '@/api/queries/dashboard/useGetDashboardProposalStats';
import { currencyLabel } from '@/helpers/currency';
import { useTranslation } from '@/i18n';
import { useAccount } from '@/utils/storeHooks';
import { Card, Tooltip } from '@payaca/ambrosia';

import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { FC } from 'react';
import { DateRange } from '../utils';

interface QuickStatProps {
  icon: TIconName;
  label: string;
  value: string | number;
  subValue?: string;
  keyFigure?: 'value' | 'subValue';
  tooltip?: string;
  iconBgClassName: string;
  iconClassName: string;
}

const QuickStat: FC<QuickStatProps> = ({
  icon,
  label,
  value,
  subValue,
  keyFigure = 'value',
  tooltip,
  iconBgClassName,
  iconClassName,
}) => (
  <div className="flex items-center gap-3">
    <div
      className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${iconBgClassName}`}
    >
      <UntitledIcon name={icon} className={`size-5 ${iconClassName}`} />
    </div>
    <div className="min-w-0">
      <p className="flex items-center gap-1 supporting-body text-gray-500">
        {label}
        {tooltip && (
          <Tooltip tooltipContent={tooltip} className="translate-y-0.5">
            <UntitledIcon
              name="info-circle.3"
              className="size-3.5 text-gray-400"
            />
          </Tooltip>
        )}
      </p>
      {keyFigure === 'subValue' && subValue ? (
        <>
          <h4>{subValue}</h4>
          <p className="supporting-body text-gray-500">{value}</p>
        </>
      ) : (
        <>
          <h4>{value}</h4>
          {subValue && (
            <p className="supporting-body text-gray-500">{subValue}</p>
          )}
        </>
      )}
    </div>
  </div>
);

interface DashboardQuickStatsProps {
  dateRange?: DateRange;
}

const DashboardQuickStats: FC<DashboardQuickStatsProps> = ({ dateRange }) => {
  const account = useAccount();
  const translate = useTranslation('dashboard.quickStats');
  const { stats: proposalStats } = useGetDashboardProposalStats({ dateRange });
  const { stats: invoiceStats } = useGetDashboardInvoiceStats({ dateRange });

  const currencyCode = currencyCodeForRegion[account?.region as AccountRegions];
  const formatCurrency = (value: number) => currencyLabel(value, currencyCode);

  // Calculate pending proposals (sent but not accepted or declined)
  const pendingProposalCount = Math.max(
    0,
    (proposalStats?.sent.count ?? 0) -
      (proposalStats?.accepted.count ?? 0) -
      (proposalStats?.declined.count ?? 0)
  );
  const pendingProposalValue = Math.max(
    0,
    (proposalStats?.sent.totalValue.value ?? 0) -
      (proposalStats?.accepted.totalValue.value ?? 0) -
      (proposalStats?.declined.totalValue.value ?? 0)
  );

  // Calculate awaiting payment (sent but not paid - simplified, doesn't account for partial)
  const awaitingPaymentCount = Math.max(
    0,
    (invoiceStats?.sent.count ?? 0) - (invoiceStats?.paid.count ?? 0)
  );
  const awaitingPaymentValue = Math.max(
    0,
    (invoiceStats?.sent.totalValue.value ?? 0) -
      (invoiceStats?.paid.totalValue.value ?? 0)
  );

  // Collection rate
  const collectionRate =
    invoiceStats?.sent.totalValue.value &&
    invoiceStats.sent.totalValue.value > 0
      ? (invoiceStats.paid.totalValue.value /
          invoiceStats.sent.totalValue.value) *
        100
      : 0;

  // Average proposal value
  const avgProposalValue =
    proposalStats?.sent.count && proposalStats.sent.count > 0
      ? proposalStats.sent.totalValue.value / proposalStats.sent.count
      : 0;

  // Average invoice value
  const avgInvoiceValue =
    invoiceStats?.sent.count && invoiceStats.sent.count > 0
      ? invoiceStats.sent.totalValue.value / invoiceStats.sent.count
      : 0;

  return (
    <Card>
      <Card.Body>
        <div className="grid grid-cols-2 gap-4 @screen-sm:grid-cols-3 @screen-lg:grid-cols-6">
          <QuickStat
            icon="file-check-02.3"
            label={translate('proposalsSent')}
            tooltip={translate('proposalsSentTooltip')}
            value={proposalStats?.sent.count ?? 0}
            subValue={formatCurrency(proposalStats?.sent.totalValue.value ?? 0)}
            keyFigure="subValue"
            iconBgClassName="bg-blue-500/10"
            iconClassName="text-blue-500"
          />
          <QuickStat
            icon="hourglass-01"
            label={translate('pendingProposals')}
            tooltip={translate('pendingProposalsTooltip')}
            value={pendingProposalCount}
            subValue={formatCurrency(pendingProposalValue)}
            keyFigure="subValue"
            iconBgClassName="bg-purple-500/10"
            iconClassName="text-purple-500"
          />
          <QuickStat
            icon="file-06.3"
            label={translate('invoicesSent')}
            value={invoiceStats?.sent.count ?? 0}
            subValue={formatCurrency(invoiceStats?.sent.totalValue.value ?? 0)}
            keyFigure="subValue"
            iconBgClassName="bg-blue-500/10"
            iconClassName="text-blue-500"
          />
          <QuickStat
            icon="clock.3"
            label={translate('awaitingPayment')}
            value={awaitingPaymentCount}
            subValue={formatCurrency(awaitingPaymentValue)}
            keyFigure="subValue"
            iconBgClassName="bg-yellow-500/10"
            iconClassName="text-yellow-500"
          />
          <QuickStat
            icon="percent-02.3"
            label={translate('collectionRate')}
            value={`${collectionRate.toFixed(0)}%`}
            subValue={translate('ofInvoicedValue')}
            iconBgClassName="bg-green-500/10"
            iconClassName="text-green-500"
          />
          <QuickStat
            icon="coins-hand.3"
            label={translate('avgDealSize')}
            tooltip={translate('avgDealSizeTooltip')}
            value={formatCurrency(avgProposalValue)}
            subValue={`${translate('invoice')}: ${formatCurrency(
              avgInvoiceValue
            )}`}
            iconBgClassName="bg-cyan-500/10"
            iconClassName="text-cyan-500"
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default DashboardQuickStats;
